import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Button, Box } from '@mui/material';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import {
  maxDistanceAtom,
  searchValuesAtom,
  selectedBranchAtom,
  userDataAtom,
} from '../../Atoms';
import SchemaWrapper, { SINGLE_SCHEMA } from '../engine/SchemaWrapper';
import { SchemaParametersProvider } from '../engine/SchemaParameters';
import {
  resolveFilterFields,
  collectFilterValues,
} from '../engine/filters/resolveFilters';
import { genericFormAtom } from '../../store/genericFormStore';
import useSectionLayout from '../../Hooks/useSectionLayout';
import { CUSTOMER_FILTERS } from '../ContactListWrapper/utils/customerSurfaces';
import { atDialect } from '../engine/dialect';

/** The panel's own filters, as the layout document the hook reads a default from. */
const DEFAULT_FILTERS = atDialect(CUSTOMER_FILTERS);

/** Where in the form store this panel's values live, under the section it is filtering. */
const FILTERS_SURFACE = 'filters';

/**
 * The filters panel of a customer list, rendered from the section's filter layout.
 *
 * Every control is a declared field: its type says how it is drawn, and a list it offers comes from
 * the registry key it names. What such a key needs to answer - which branch, which section - is
 * published here, along with the furthest customer, which is the end of the distance scale.
 *
 * @param {Object} props - Component props.
 * @param {String} props.currentSection - The section being filtered, whose layout is read.
 * @param {Array} props.layout - The layout the list is filtered by, where its page resolves one; the
 *   panel reads the section's own otherwise.
 * @param {Object} props.defaultFilters - Currently applied filters, used to seed the panel.
 * @param {Function} props.closeMenu - Executed once the filters are applied.
 *
 * @returns {React.JSX.Element}
 */
const FiltersSelection = function FiltersSelection({
  currentSection = 'CUSTOMERS',
  layout: pageLayout = null,
  defaultFilters = {},
  closeMenu,
}) {
  const intl = useIntl();
  // The page's layout where it has one: read here against the generic default, a section that
  // declares its own filters - the day's called indicator, not the month's - drew keys its list
  // does not send.
  const sectionLayout = useSectionLayout(
    pageLayout ? null : currentSection,
    'filters',
    DEFAULT_FILTERS
  );
  const layout = pageLayout ?? sectionLayout;

  const setSearchValues = useSetAtom(searchValuesAtom);
  const [payload, setPayload] = useAtom(genericFormAtom);

  const selectedBranch = useAtomValue(selectedBranchAtom);
  const userData = useAtomValue(userDataAtom);
  const maxDistance = useAtomValue(maxDistanceAtom);

  // The runtime values the panel publishes: what a lookup key or a condition needs to answer for
  // this branch, section and user, and the far end of the distance scale.
  const parameters = useMemo(
    () => ({
      Section: currentSection,
      BranchId: selectedBranch?.id,
      UserId: userData?.id,
      Locale: intl.locale,
      maxDistance: maxDistance ? Math.ceil(maxDistance) : undefined,
    }),
    [currentSection, selectedBranch, userData, intl.locale, maxDistance]
  );

  const groups = useMemo(
    () => [
      {
        id: currentSection,
        items: [
          {
            id: FILTERS_SURFACE,
            fields: resolveFilterFields(layout, { values: defaultFilters }),
          },
        ],
      },
    ],
    [currentSection, layout, defaultFilters]
  );

  const handleFilterChange = () => {
    const values = payload?.[currentSection]?.[FILTERS_SURFACE] ?? {};

    setSearchValues((prev) => ({
      ...prev,
      ...collectFilterValues(layout, values, parameters),
    }));
    closeMenu();
  };

  // Clearing empties both what is applied and what the panel shows, which are two places: the
  // fields read the store they write to, not the filters.
  const handleClear = () => {
    setPayload((held) => ({ ...held, [currentSection]: {} }));
    setSearchValues((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, value]) => [
          key,
          key === 'sortValue' || key === 'searchBox' ? value : null,
        ])
      )
    );
  };

  return (
    <SchemaParametersProvider parameters={parameters}>
      {/* Wide enough for the two-column layout the filter fields declare, and never wider than the
          screen: at 35vw the panel was a third of an ultrawide monitor and too narrow on a laptop. */}
      <Box sx={{ width: 'min(600px, 92vw)', p: '8px 16px' }}>
        <SchemaWrapper
          groups={groups}
          tabValue={currentSection}
          {...SINGLE_SCHEMA}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          position: 'sticky',
          bottom: 0,
          padding: '14px 20px calc(env(safe-area-inset-bottom) + 16px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        {/* Surface and divider tokens rather than #fff and black alphas: the bar is drawn over the
            dialog's own paper, which is dark in the dark theme. */}
        <Button
          id="clear-filters-btn"
          onClick={handleClear}
          disableElevation
          sx={{
            flex: 'none',
            width: 110,
            height: 48,
            bgcolor: 'background.paper',
            color: 'text.secondary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '26px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 15,
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'text.disabled',
            },
          }}
        >
          {intl.messages.CLEAR}
        </Button>
        <Button
          id="confirm-filters-btn"
          onClick={handleFilterChange}
          disableElevation
          sx={{
            flex: 1,
            height: 48,
            borderRadius: '26px',
            // The theme's own gradient, so the panel follows the one the sidebar and the mobile
            // header already draw instead of keeping a second copy of the same two colours.
            background: (theme) => theme.custom.gradients.brand,
            color: 'common.white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 16,
            letterSpacing: '.02em',
            boxShadow: '0 8px 20px rgba(29,91,157,.35)',
            '&:hover': {
              background: (theme) => theme.custom.gradients.brand,
              boxShadow: '0 8px 20px rgba(29,91,157,.35)',
            },
          }}
        >
          {intl.messages.CONFIRM}
        </Button>
      </Box>
    </SchemaParametersProvider>
  );
};

export default FiltersSelection;
